import classNames from "classnames"
import PropTypes from "prop-types"
import { Paper } from "@mui/material"
import { map } from "lodash"
import { DATE } from "helpers/shapes"
import CompareChart from "components/charts/compare"
import CompareTable from "components/compare-table"
import CompareSelect from "components/material/compare-select"
import { AGGREGATION_SHAPE } from "helpers/factorClassification"
import AggregationDropdown, { OPTION_SHAPE } from "components/list-item-dropdown"
import { FACTOR_CATEGORIES_SHAPE } from "components/factor-categories/shapes"
import { percentageFormatter } from "helpers/formatters/numbers"
import { FACTOR_SENSITIVITY_TYPES, getMetricFromFactorSensitivityType } from "helpers/factorSensitivity"
import { DATA, DOWNLOAD_FILENAME, METRICS, SELECTED_METRIC, TABLE_DOWNLOAD_DATA } from "helpers/shapes/compare-screen"
import "./styles.scss"

export default function CompareFactorSensitivity({
  status,
  data,
  startDate,
  endDate,
  selectedDate,
  setDate,
  metrics,
  selectedMetric,
  setMetric,
  items,
  detailsItems,
  detailsData,
  saveMetricToStore,
  trackPanelTrigger,
  fixedItemNumber,
  hideGraph,
  displayAggregationToggle,
  setAggregationFn,
  aggregation,
  aggregationOptions,
  factorCategories,
  factorSensitivityType,
  fileName,
  tableDownloadData,
}) {
  const metricStore = getMetricFromFactorSensitivityType(factorSensitivityType)

  return <Paper className={classNames("modules_compare-factor-sensitivity", { "linked-model": displayAggregationToggle })} square elevation={1}>
    <div className="wrapper">
      {displayAggregationToggle && <AggregationDropdown setValue={setAggregationFn} value={aggregation} listSubheader="Choose display type for style factors" label="Display Type" options={aggregationOptions} classes={{ toggle: "aggregation-toggle" }} />}
      {!hideGraph && <CompareChart
        tooltipFormatter={percentageFormatter}
        metricSelector={<CompareSelect
          options={metrics}
          selectedOption={selectedMetric}
          setSelected={setMetric}
          saveToStore={saveMetricToStore}
          storeName={metricStore}
        />}
        {...{
          status,
          tickFormat: percentageFormatter,
          startDate,
          date: selectedDate,
          setDate,
          endDate,
          data,
          items,
          fixedItemNumber,
          fileName,
          selectedView: metricStore,
          tableDownloadData,
        }}
      />}
      <CompareTable
        className={classNames({ "single-date": hideGraph })}
        firstCategoryOpened
        {...{
          items: detailsItems,
          data: detailsData,
          columns: items,
          trackPanelTrigger,
          fixedItemNumber,
          factorCategories,
        }}
      />
    </div>
  </Paper>
}

CompareFactorSensitivity.propTypes = {
  status: PropTypes.string.isRequired,
  data: DATA.isRequired,
  startDate: DATE.isRequired,
  endDate: DATE.isRequired,
  selectedDate: DATE,
  setDate: PropTypes.func.isRequired,
  metrics: METRICS.isRequired,
  selectedMetric: SELECTED_METRIC.isRequired,
  setMetric: PropTypes.func.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    noData: PropTypes.bool,
    isUmodeled: PropTypes.bool,
  })).isRequired,
  detailsItems: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    color: PropTypes.string,
    defaultOpen: PropTypes.bool,
    fn: PropTypes.func,
    items: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      fn: PropTypes.func.isRequired,
    })),
  })).isRequired,
  detailsData: PropTypes.arrayOf(PropTypes.shape({
    factors: PropTypes.object, // factors keyed by factor id { AU: 1, BM: 2, ...}
  })).isRequired,
  saveMetricToStore: PropTypes.func.isRequired,
  trackPanelTrigger: PropTypes.func.isRequired,
  fixedItemNumber: PropTypes.bool,
  hideGraph: PropTypes.bool,
  displayAggregationToggle: PropTypes.bool,
  setAggregationFn: PropTypes.func,
  aggregation: AGGREGATION_SHAPE,
  aggregationOptions: PropTypes.arrayOf(OPTION_SHAPE),
  factorCategories: FACTOR_CATEGORIES_SHAPE,
  factorSensitivityType: PropTypes.oneOf(map(FACTOR_SENSITIVITY_TYPES)).isRequired,
  fileName: DOWNLOAD_FILENAME,
  tableDownloadData: TABLE_DOWNLOAD_DATA,
}
